/**
 * `W5-T12` §4.10, §7 — charge to ask, the reads and writes every screen shares: what asking costs,
 * what the owner paid for on a job, the two checkouts, and what a refusal means. Keys, never
 * sentences; amounts are the API's integer cents, never computed here (charter rule 2).
 *
 * **Leaving for Stripe is a redirect, not a `window` call** (`features/payouts/onboarding.ts`): React
 * Router turns a `redirect()` to another origin into `window.location.assign(url)`. Stripe.js is not
 * loaded here, nor anywhere under `features/checkout/ask` (AC 43, `MEM-2026-10-17-money-1`): Checkout
 * is a hosted page.
 */
import { redirect, type LoaderFunctionArgs } from 'react-router';
import {
  type AskPrices,
  type HostedRedirect,
  type JobAsk,
  type PublishCheckoutInput,
} from '@marketplace/contracts';
import { ApiError } from '../../../shared/api-error.js';
import { queryKeys, routeContext } from '../../../shared/query.js';
import { type AuctionChoice } from '../../auctions/auction-form.js';

type RouteContext = LoaderFunctionArgs['context'];

/** Where Checkout came back to (§4.3 step 7): `success_url` and `cancel_url` say it. */
export type AskReturn = 'paid' | 'cancelled';

/**
 * The prices as a page reads them: the API's answer; `'unavailable'` when charging is on and a price
 * failed its check (`503 PAYMENTS_UNAVAILABLE`, §4.1); `null` when they could not be read at all, in
 * which case the page shows today's buttons and the API judges (`ASK_PAYMENT_REQUIRED`, §4.5).
 */
export type AskPricesRead = AskPrices | 'unavailable' | null;

/** Charging is on and both prices are there to show. */
export type AskCharging = AskPrices & {
  chargeEnabled: true;
  quoteWindow: NonNullable<AskPrices['quoteWindow']>;
  auction: NonNullable<AskPrices['auction']>;
};

/** What a checkout write answers the page when it does not leave for Stripe. */
export interface AskActionData {
  error: string;
}

export const ASK_UNAVAILABLE = 'ask.unavailable';

/** §4.1: the prices are cached for five minutes on the server; the page keeps them as long. */
const PRICES_FRESH_MS = 5 * 60_000;

export function askReturnOf(url: string): AskReturn | null {
  const value = new URL(url).searchParams.get('ask');
  return value === 'paid' || value === 'cancelled' ? value : null;
}

export function charging(prices: AskPricesRead): prices is AskCharging {
  return (
    prices !== null &&
    prices !== 'unavailable' &&
    prices.chargeEnabled &&
    prices.quoteWindow !== null &&
    prices.auction !== null
  );
}

export async function loadAskPrices(context: RouteContext): Promise<AskPricesRead> {
  const { queryClient, api } = context.get(routeContext);
  try {
    return await queryClient.fetchQuery({
      queryKey: queryKeys.askPrices(),
      queryFn: () => api.getAskPrices(),
      staleTime: PRICES_FRESH_MS,
      // A 503 is the server's decision, said at once; nothing is gained by asking again.
      retry: false,
    });
  } catch (error) {
    return isUnavailable(error) ? 'unavailable' : null;
  }
}

/**
 * What the owner paid for on the job, fresh: a window fills and a refund lands while the page is
 * open, and the poll is how it finds out. `null` when it could not be read — the panel is left out.
 */
export async function loadJobAsk(context: RouteContext, jobId: string): Promise<JobAsk | null> {
  const { queryClient, api } = context.get(routeContext);
  return queryClient
    .fetchQuery({
      queryKey: queryKeys.jobAsk(jobId),
      queryFn: () => api.getJobAsk(jobId),
      staleTime: 0,
    })
    .catch(() => null);
}

/** The posting form's choice, as the checkout's body: the auction's parameters ride along (§4.3). */
export function publishCheckoutInput(choice: AuctionChoice): PublishCheckoutInput {
  return choice.mode === 'AUCTION' ? { mode: 'AUCTION', ...choice.input } : { mode: 'QUOTES' };
}

export const jobPath = (locale: string, jobId: string, ask?: AskReturn): string =>
  `/${locale}/jobs/${jobId}${ask === undefined ? '' : `?ask=${ask}`}`;

/**
 * Start a checkout and leave for it.
 *
 * - the hosted page → the redirect, but only to Checkout's own host (the server checks it too,
 *   §4.3 step 8; a page that can never send the client elsewhere costs one line);
 * - `409 ASK_PAYMENT_CONFIRMING` → the job's page as Checkout's return finds it (`?ask=paid`, §7);
 * - `503 PAYMENTS_UNAVAILABLE` (or `ASK_PAYMENT_REQUIRED`, §7) → `ask.unavailable`;
 * - another `409` (`CONFLICT`, `QUOTE_WINDOW_HAS_ROOM`) → the job is read again and shown as it is;
 * - anything else → `fallback`.
 */
export async function startAskCheckout(
  context: RouteContext,
  locale: string,
  jobId: string,
  start: () => Promise<HostedRedirect>,
  fallback: string,
): Promise<Response | AskActionData | null> {
  const { queryClient } = context.get(routeContext);
  try {
    const { url } = await start();
    return isCheckoutPage(url) ? redirect(url) : { error: ASK_UNAVAILABLE };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return redirect(`/${locale}/login`);
    if (error instanceof ApiError && error.message === 'ASK_PAYMENT_CONFIRMING') {
      return redirect(jobPath(locale, jobId, 'paid'));
    }
    if (isUnavailable(error)) return { error: ASK_UNAVAILABLE };
    if (error instanceof ApiError && error.status === 409) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.job(jobId), refetchType: 'all' });
      return null;
    }
    return { error: fallback };
  }
}

/**
 * The job page's two charge-to-ask writes, told apart by `intent`: pay to publish the draft
 * (presupuestos — the draft page offers no auction, as before), and ask for another window (no body).
 * `undefined` for any other intent, which the route handles itself.
 */
export async function runAskAction(
  context: RouteContext,
  locale: string,
  jobId: string,
  form: FormData,
): Promise<Response | AskActionData | null | undefined> {
  const { api } = context.get(routeContext);
  const intent = form.get('intent');
  if (intent === 'ask-publish') {
    return startAskCheckout(
      context,
      locale,
      jobId,
      () => api.startPublishCheckout(jobId, { mode: 'QUOTES' }),
      'job.publish.error',
    );
  }
  if (intent === 'ask-window') {
    return startAskCheckout(
      context,
      locale,
      jobId,
      () => api.startQuoteWindowCheckout(jobId),
      ASK_UNAVAILABLE,
    );
  }
  return undefined;
}

function isUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.message === 'PAYMENTS_UNAVAILABLE' || error.message === 'ASK_PAYMENT_REQUIRED')
  );
}

/** Checkout's origin: `https`, its host, its default port — compared whole. */
const CHECKOUT_ORIGIN = 'https://checkout.stripe.com';

function isCheckoutPage(url: string): boolean {
  try {
    return new URL(url).origin === CHECKOUT_ORIGIN;
  } catch {
    return false;
  }
}
